import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { PiToolboxFill } from 'react-icons/pi';
import { FaSquareXTwitter, FaRegFilePdf, FaWhatsapp } from 'react-icons/fa6';
import { CiInstagram } from 'react-icons/ci';
import { IoLogoFacebook, IoLogoLinkedin } from 'react-icons/io';
import { useBusinessData } from '../../hooks/BusinessManagement/useBusinessData';
import { useBusinessActions } from '../../hooks/BusinessManagement/useBusinessActions';
import { TransactionTable } from '../../components/transcations/TranscationTable';
import getImage from '../../assets/Assets';
import { showError } from '../Ui/ToastNotification';
import {
  fetchBusinessData,
  processQuotesData,
} from '../../services/BusinessManagement/BusinessProfileService';
import ActivityBarChart from '../analytics/BusinessProfileChart';
import BusinessReviewCarousel from './BusinessReviewCarousel';
import { formatDate, formatYear } from '../../utils/Date';
import Title from '../Title';
import PDFViewer from '../PDFViewer';
import {
  fetchSubscriptions,
  transformSubscriptionsToTransactions,
} from '../../hooks/useTable';
import { BeatLoader } from 'react-spinners';

const BusinessProfile = () => {
  const { id } = useParams();
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const locationImage = getImage('location');
  const webImage = getImage('web');
  const calenderImage = getImage('calender');
  const calender2 = getImage('calender2');
  const location2 = getImage('location2');
  const {
    data: business,
    loading,
    refetchData,
  } = useBusinessData(fetchBusinessData, processQuotesData, id);
  const { handleStatusChange } = useBusinessActions(id, refetchData);

  const handleApprove = () => handleStatusChange('approve');
  const handleDeny = () => handleStatusChange('deny');
  const handleDeactivate = () => handleStatusChange('deactivate');

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSubscriptions(id);
        const subscriptions = data?.subscriptions || [];
        const transformed = transformSubscriptionsToTransactions(subscriptions);
        setTransactions(transformed);
      } catch (error) {
        console.error('Error loading subscriptions:', error);
        setTransactions([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [id]);

  if (loading) {
    return (
      <div
        data-testid="loading-spinner"
        className="p-4 sm:ml-64 w-[80%] h-screen bg-[#F4F5F6] flex justify-center items-center"
      >
        <BeatLoader color="#000000" size={15} />
      </div>
    );
  }
  if (isLoading) {
    return (
      <div
        data-testid="loading-spinner"
        className="p-4 sm:ml-64 w-[80%] h-screen bg-[#F4F5F6] flex justify-center items-center"
      >
        <BeatLoader color="#000000" size={15} />
      </div>
    );
  }

  const businessData = business;
  const representatives = businessData?.representatives || [];
  const sociallink = businessData?.sociallink || [];
  const documents = businessData?.documents || [];

  const socialIcons = {
    linkedin: <IoLogoLinkedin />,
    facebook: <IoLogoFacebook />,
    whatsapp: <FaWhatsapp />,
    twitter: <FaSquareXTwitter />,
    instagram: <CiInstagram />,
  };

  const handleViewDocument = (fileUrl) => {
    try {
      if (fileUrl) {
        console.log('Opening document:', fileUrl);
        setSelectedDoc(fileUrl);
      } else {
        showError('Document URL is not available');
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      showError('Failed to open the document. Please try again later.');
    }
  };

  const closeViewer = () => {
    setSelectedDoc(null);
  };

  return (
    <div className="p-3 sm:ml-64 w-[80%] bg-[#F4F5F6] font-nohemi">
      <div className="">
        <div className="flex items-center justify-between mb-6">
          <div className="text-nowrap">
            <Title
              title="Business Listing"
              subtitle="Companies/Business &gt; Profile"
            />
          </div>
        </div>
        <div className="flex flex-row w-full mx-auto ">
          <div className="relative w-1/4 mx-1 rounded-t-2xl">
            <div className="w-full bg-white h-[200px] relative z-10">
              <img
                className="object-cover w-full h-full rounded-lg"
                src={
                  businessData?.media?.[1]?.fileUrl ||
                  'https://images.unsplash.com/photo-1632349069255-3b55e28e6a49?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8cGxhaW4lMjBibGFjayUyMGJhY2tncm91bmR8ZW58MHx8MHx8fDA%3Dhttps://unsplash.com/photos/black-textile-on-white-background-jqKS0ET-wGE'
                }
                alt="Business media"
              />
            </div>

            <div className="relative z-20 mt-[-60px] pb-4 w-[98%] bg-white rounded-lg mx-auto">
              <div className="items-start justify-center w-full mt-6">
                <p className="text-base font-medium text-[#404D61] mx-4 py-2">
                  Business Owner Information
                </p>
              </div>

              <div className="w-full">
                <div className="w-[80%] mx-4 my-2">
                  <div className="my-3">
                    <p className="text-sm font-medium text-[#000000] mb-1">
                      Owner Name
                    </p>
                    <p className="text-xs font-light text-[#757575]">
                      {representatives[0]?.firstName
                        ? `${representatives[0].firstName} ${
                            representatives[0]?.lastName || ''
                          }`.trim()
                        : 'Not Available'}
                    </p>
                  </div>

                  <div className="my-3">
                    <p className="text-sm font-medium text-[#000000] mb-1">
                      Contact Information
                    </p>
                    <p className="text-xs font-light text-[#757575]">
                      {representatives[0]?.phoneNumber || 'Not Available'}
                    </p>
                  </div>

                  <div className="my-3">
                    <p className="text-sm font-medium text-[#000000] mb-1">
                      Email
                    </p>
                    <p className="text-xs font-light text-[#757575]">
                      {representatives[0]?.email ||
                        businessData?.customEmail ||
                        'Not Available'}
                    </p>
                  </div>

                  <div className="my-3">
                    <p className="text-sm font-medium text-[#000000] mb-1">
                      Address
                    </p>
                    <p className="text-xs font-light text-[#757575]">
                      {businessData?.team_Size || 'Not Available'}
                    </p>
                  </div>

                  <div className="my-3">
                    <p className="text-sm font-medium text-[#000000] mb-1">
                      Tax ID
                    </p>
                    <p className="text-xs font-light text-[#757575]">
                      {business?.TIN || 'Not Available'}
                    </p>
                  </div>

                  <div className="my-3">
                    <p className="text-sm font-medium text-[#000000] mb-1">
                      Title in organization
                    </p>
                    <p className="font-light text-xs text-[#757575]">
                      {representatives[0]?.roleInOrganization ||
                        'Not Available'}
                    </p>
                  </div>

                  <div className="my-2">
                    <p className="text-sm font-medium text-[#000000]">
                      Social handles
                    </p>
                    {sociallink?.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {sociallink.slice(0, 5).map((social) => {
                          const { platform, url } = social;
                          const lowerPlatform = platform?.toLowerCase();
                          const icon = socialIcons[lowerPlatform];
                          const isTwitter = lowerPlatform === 'twitter';

                          return (
                            <a
                              key={social._id}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <div
                                className={`p-2 rounded-lg flex items-center gap-2 ${
                                  isTwitter
                                    ? 'bg-black text-white'
                                    : 'bg-[#CCCCCC] text-white'
                                }`}
                              >
                                <div className="w-4 h-4 text-2xl">{icon}</div>
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs font-light text-[#757575] mt-2">
                        No social handles available
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="w-3/4 ml-2">
            <div className="bg-[#F8F9FA] rounded-lg">
              <div className="flex justify-between p-3 px-4">
                <h2 className=" text-xl font-bold text-[#4C4C4C]">
                  {businessData?.name}
                </h2>
                <div className="flex flex-row">
                  {businessData?.applicationStatus === 'Approved' &&
                  businessData?.status === 'active' ? (
                    <button
                      onClick={handleDeactivate}
                      className="text-xs bg-black text-white inline-flex items-center justify-center py-1 px-2.5 mx-1 text-center font-light rounded-md focus:ring-0"
                    >
                      Deactivate Business
                    </button>
                  ) : businessData?.applicationStatus === 'Approved' &&
                    (businessData?.status === 'disable' ||
                      !businessData?.status) ? (
                    <button
                      onClick={() => handleStatusChange('reactivate')}
                      className="text-xs bg-green-600 text-white inline-flex items-center justify-center py-1 px-2.5 mx-1 text-center font-light rounded-md focus:ring-0"
                    >
                      Activate Business
                    </button>
                  ) : businessData?.applicationStatus === 'Pending' ||
                    businessData?.applicationStatus === 'Denied' ? (
                    <>
                      <button
                        onClick={handleApprove}
                        className="text-xs rounded-md bg-black text-white inline-flex items-center justify-center py-1 px-2.5 mx-1 text-center font-light focus:ring-0"
                      >
                        Approve
                      </button>
                      <button
                        onClick={handleDeny}
                        className="text-xs rounded-md bg-transparent text-black inline-flex items-center justify-center py-1 px-2.5 mx-1 text-center font-light focus:ring-0 border border-black"
                      >
                        Deny
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleStatusChange('reactivate')}
                      className="text-xs bg-green-600 text-white inline-flex items-center justify-center py-1 px-2.5 mx-1 text-center font-light rounded-md focus:ring-0"
                    >
                      Activate Business
                    </button>
                  )}
                </div>
              </div>
              <div className="w-full pb-2 m-auto bg-white rounded-lg">
                <div className="flex flex-row h-full">
                  <div className="flex justify-center items-center w-2/3 max-w-[600px]">
                    <div className="flex flex-col w-full h-full p-4 border-r">
                      <div className="flex flex-row space-x-4">
                        <div className="flex-shrink-0 w-24 h-24">
                          {businessData?.media?.[0]?.fileUrl && (
                            <img
                              className="object-cover w-full h-full rounded-lg"
                              src={businessData?.media[0]?.fileUrl}
                              alt="Business media"
                            />
                          )}
                        </div>
                        <div className=" flex flex-col space-y-2 mt-1.5">
                          <p className="text-base font-semibold text-[#1E1E1E]">
                            {businessData?.industryType?.name || '-'}
                          </p>
                          <p className="text-xs font-light inline-flex text-[#727272]">
                            <img
                              src={location2}
                              alt="location"
                              className="w-3 h-3 mr-2"
                            />
                            {businessData?.locationObject?.address ||
                              'No available location to be displayed'}
                          </p>
                          <p className="text-xs inline-flex font-light text-[#727272] items-center">
                            <img
                              src={calender2}
                              alt="calender"
                              className="w-3 h-3 mr-2"
                            />
                            Est.{' '}
                            {formatYear(businessData?.yearEstablished) || '-'}
                          </p>
                          <div className="flex space-x-2 text-xs font-light">
                            {businessData?.sociallink
                              ?.slice(0, 5)
                              .map((social, index) => {
                                const { platform, url } = social;
                                const lowerPlatform = platform?.toLowerCase();
                                const icon = socialIcons[lowerPlatform];

                                return (
                                  <a
                                    key={`social-${index}`}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <span
                                      className={`inline-flex items-center ${
                                        lowerPlatform === 'instagram'
                                          ? 'text-[#EA3843]'
                                          : ''
                                      }`}
                                    >
                                      {icon &&
                                        React.cloneElement(icon, {
                                          className: 'w-3 h-3 mx-0.5',
                                        })}
                                      {lowerPlatform}
                                    </span>
                                  </a>
                                );
                              })}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 w-[90%]">
                        <p className=" text-base font-semibold text-[#191B1C]">
                          Business Description
                        </p>
                        <p className=" font-light text-xs text-[#7B878C] line-clamp-5">
                          {businessData?.bio || '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex w-1/3">
                    <div className="mt-6 ml-4">
                      <p className="text-base font-semibold mb-2 text-[#1E1E1E]">
                        Basic Info
                      </p>
                      <div className="flex text-[12px] mx-1 my-2 font-light text-[#1E1E1E]">
                        <img
                          src={locationImage}
                          alt="location"
                          className="w-3 h-3"
                        />
                        <p className="text-xs mx-2 font-light text-[#686575]">
                          {businessData?.location || '-'}
                        </p>
                      </div>
                      <div className="flex text-[12px] mx-1 my-2 font-light text-[#1E1E1E]">
                        <img src={webImage} alt="web" className="w-3 h-3" />
                        <p className="text-xs mx-2 font-light text-[#686575]">
                          {businessData?.website || '-'}
                        </p>
                      </div>
                      <div className="flex text-[12px] mx-1 my-2 font-light text-[#1E1E1E]">
                        <img
                          src={calenderImage}
                          alt="calender"
                          className="w-3 h-3"
                        />
                        <p className="text-xs mx-2 font-light text-[#686575]">
                          {formatDate(
                            businessData?.founded_year ||
                              businessData?.yearEstablished,
                          ) || '-'}
                        </p>
                      </div>
                      <div className="flex text-[12px] mx-1 my-2 font-light text-[#1E1E1E]">
                        <PiToolboxFill />
                        <p className="text-xs mx-2 font-light text-[#686575]">
                          {businessData?.serviceType?.name || '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full bg-white rounded-lg mt-4 px-4 py-6 h-[290px]">
              <div className="m-auto">
                <p className=" text-base font-semibold text-[#191B1C]">
                  Types of Services Offered
                </p>
                <div className="flex flex-row mt-2">
                  {(businessData?.serviceFocus?.length > 0
                    ? businessData.serviceFocus
                    : businessData?.serviceType
                      ? [businessData.serviceType]
                      : []
                  ).map((service) => (
                    <button
                      key={service.id || service._id || service}
                      className="bg-[#F4F4F4] rounded-lg inline-flex items-center justify-center p-2.5 mx-1 mb-2 text-center text-[11px] font-light text-[#404D61] focus:ring-0"
                    >
                      {service.name || service}
                    </button>
                  ))}

                  {!businessData?.serviceFocus?.length &&
                    !businessData?.serviceType && (
                      <p className="text-[11px] text-gray-500">
                        No services listed
                      </p>
                    )}
                </div>
              </div>
              <div className="mt-4">
                <p className=" text-base font-semibold text-[#191B1C] mb-2">
                  Target Market Segments
                </p>
                {businessData?.industryType?.tags?.map((tag, index) => (
                  <button
                    key={tag.id || index}
                    className="bg-[#F4F4F4] rounded-lg inline-flex items-center justify-center p-2.5 mx-1 mb-2 text-center text-[11px] font-light text-[#404D61] focus:ring-0"
                  >
                    {tag}
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <p className="text-base font-semibold text-[#191B1C]">
                  Geographical Coverage
                </p>
                <div className="flex flex-row mt-2">
                  {businessData?.other_location &&
                  businessData?.other_location.length > 0 ? (
                    businessData?.other_location.map((area, index) => (
                      <button
                        key={area._id || index}
                        className="bg-[#F4F4F4] rounded-lg inline-flex items-center justify-center p-2.5 mx-1 mb-2 text-center text-[11px] font-light text-[#404D61] focus:ring-0"
                      >
                        {area}
                      </button>
                    ))
                  ) : (
                    <p className="text-xs text-gray-500">
                      Other Locations not available
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Document section */}
        <div className="p-2 mt-4 bg-white">
          <div className="w-full bg-white rounded-lg mt-2 h-[270px]">
            <div className="items-start justify-center px-2">
              <p className="text-base font-semibold text-[#191B1C] px-2">
                Company documents
              </p>
            </div>
            <div className="flex flex-row items-center justify-center gap-6 py-8">
              {documents?.length > 0 ? (
                documents.map((document, index) => (
                  <button
                    key={document._id}
                    onClick={() => handleViewDocument(document.fileUrl)}
                    className="w-80 h-32 flex items-center justify-center border-dashed border-2 border-[#CBD5E1] rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="text-center">
                      <div className="flex items-center justify-center w-10 h-10 mx-auto my-2 rounded-full">
                        <FaRegFilePdf className="w-6 h-6 text-red-500" />
                      </div>
                      <p className="mb-2 text-xs font-normal text-blue-500">
                        View document
                      </p>
                    </div>
                  </button>
                ))
              ) : (
                <p className="text-gray-500">No documents available</p>
              )}
            </div>
          </div>
        </div>

        {/* PDF Viewer Modal with React-PDF */}
        {selectedDoc && (
          <PDFViewer fileUrl={selectedDoc} onClose={closeViewer} />
        )}
        <div className="p-2 mt-4 bg-white">
          <div className="items-start justify-center p-2">
            <p className="text-base font-semibold text-[#191B1C] px-2">
              Business Transactions
            </p>
          </div>
          <TransactionTable
            transactions={transactions}
            businessData={businessData}
          />
          <p className="text-right font-light text-xs text-[#7B878C] mt-2">
            See All
          </p>
        </div>
        {/* Business Statistics Container */}
        <div className="p-2 my-4 bg-white ">
          <div className="w-full mt-2 h-[400px]">
            <ActivityBarChart businessId={id} />
          </div>
        </div>

        {/* Business Review Container */}
        <div className="mt-6 bg-white rounded-lg shadow-sm">
          {businessData?.review ? (
            <BusinessReviewCarousel
              businessData={businessData}
              handleDelete={(id) => console.log('Delete rating:', id)}
            />
          ) : (
            <div className="flex items-center justify-center h-40 text-gray-500">
              No data available for the selected period
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessProfile;
